import { Injectable } from '@angular/core';
import { Marked, type RendererObject, type Tokens } from 'marked';

export interface MarkdownRenderOptions {
  copyBlockquotes?: boolean;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Service centralisé de rendu Markdown → HTML.
 *
 * Remplace les 3 instances `markdown-it` historiques (kng-rules-console,
 * kng-history, kng-memories) par une seule instance MarkedJS partagée.
 *
 * MarkedJS est déjà embarqué via `@tiptap/markdown` (peer dep) — aucun
 * coût bundle additionnel par rapport à l'éditeur.
 *
 * Profil unique : `{ gfm: true, breaks: true }`.
 * - `gfm`     : tables, autolinks, strikethrough, task lists.
 * - `breaks`  : retour à la ligne simple → `<br>` (cohérent avec l'usage
 *               messages chat / résumés mémoire).
 *
 * HTML brut autorisé par défaut (équivalent `html: true` de markdown-it),
 * compatible avec les 3 consommateurs actuels.
 *
 * Différences à noter par rapport à markdown-it (cf. plan migration §1.5) :
 * - Pas de `typographer` (smart quotes, tirets) — invisible UX.
 * - `linkify` GFM standard uniquement (URLs avec protocole). Si retour
 *   utilisateur sur des URLs sans `http://` non détectées, ajouter
 *   `marked-linkify-it` en correctif ciblé.
 */
@Injectable({ providedIn: 'root' })
export class MarkdownRenderService {
  private readonly marked = new Marked({
    gfm: true,
    breaks: true,
    renderer: this.createRenderer()
  });

  render(markdown: string | null | undefined, options: MarkdownRenderOptions = {}): string {
    if (!markdown) return '';
    const html = this.marked.parse(markdown) as string;
    return options.copyBlockquotes ? this.addBlockquoteCopyButtons(html) : html;
  }

  async renderMermaid(root: ParentNode | null | undefined): Promise<void> {
    if (!root || typeof window === 'undefined') {
      return;
    }

    const nodes = Array.from(root.querySelectorAll<HTMLElement>('.mermaid:not([data-processed="true"])'));
    if (!nodes.length) {
      return;
    }

    const mermaid = (await import('mermaid')).default;
    mermaid.initialize({ startOnLoad: false, securityLevel: 'loose' });
    await mermaid.run({ nodes, suppressErrors: true });
  }

  async handleMarkdownClick(event: Event): Promise<boolean> {
    const target = event.target as HTMLElement | null;
    const copyButton = target?.closest('[data-markdown-copy="blockquote"]') as HTMLElement | null;
    if (!copyButton) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    await this.copyElementContent(copyButton.closest('blockquote') as HTMLElement | null);
    const icon = copyButton.querySelector('sl-icon');
    icon?.setAttribute('name', 'clipboard-check');
    copyButton.setAttribute('data-copied', 'true');
    copyButton.setAttribute('title', 'Copié !');
    copyButton.setAttribute('aria-label', 'Citation copiée');
    setTimeout(() => {
      icon?.setAttribute('name', 'clipboard');
      copyButton.removeAttribute('data-copied');
      copyButton.setAttribute('title', 'Copier la citation');
      copyButton.setAttribute('aria-label', 'Copier la citation');
    }, 2000);
    return true;
  }

  async copyElementContent(element: HTMLElement | null | undefined): Promise<void> {
    if (!element) {
      return;
    }

    const clone = element.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('[data-markdown-copy]').forEach(node => node.remove());
    const htmlContent = clone.innerHTML;
    const textContent = (clone.textContent || '').trim();

    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' })
        })
      ]);
      return;
    }

    await navigator.clipboard.writeText(textContent);
  }

  private createRenderer(): RendererObject {
    return {
      code(token: Tokens.Code): string | false {
        if ((token.lang || '').trim().toLowerCase() !== 'mermaid') {
          return false;
        }
        return `<div class="mermaid">${escapeHtml(token.text)}</div>`;
      }
    };
  }

  private addBlockquoteCopyButtons(html: string): string {
    return html.replace(/<blockquote\b([^>]*)>/g,
      '<blockquote$1>' +
      '<button type="button" class="copy-blockquote-btn" data-markdown-copy="blockquote" title="Copier la citation" aria-label="Copier la citation">' +
      '<sl-icon name="clipboard"></sl-icon>' +
      '</button>'
    );
  }
}
